import React, { useState, useEffect, memo } from 'react';
import cn from 'classnames';
import { Radio, Input, Select } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import styles from './controls.less';

const RadioGroup = Radio.Group;

const Radiobutton = ({
  value,
  config,
  onChange,
  onEndEditing,
  editable = true,
  placeholder,
  controlConfig,
  ...rest
}) => {
  const { t } = useTranslation();
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  const onChangeItem = async (itemId) => {
    if (!editable) return;

    // вызываем onChange из controlConfig
    const configOnChange = controlConfig?.get('onChange');
    if (typeof configOnChange === 'function') {
      try {
        await configOnChange(itemId);
      } catch (e) {
        console.error('Error in controlConfig.onChange:', e);
      }
    }

    // обновляем локальное значение
    setLocalValue(itemId);

    // вызываем внешний onChange
    if (onChange) onChange(itemId);
    if (onEndEditing) onEndEditing(itemId);
  };

  let items = config.get('items');
  const mode = config.get('mode');
  const type = config.get('type');

  let content;

  // Если не редактируем — показываем только выбранное значение
  if (!editable) {
    items = items.filter((item) => item.get('id') === value);
  }

  const manyItems =
    ((items && items.size) >= 7 && type !== 'radio' && type !== 'radiobuttonGroup') ||
    mode === 'dropdown' ||
    type === 'select' ||
    type === 'radiobuttonSelect';

  // SELECT MODE
  if (manyItems) {
    if (!editable) {
      const checkedItem = items.find((item) => item.get('id') === value);
      return <Input className={styles.radioSelectReadOnly} readOnly value={checkedItem?.get('name')} />;
    }

    const options = items.map((item) => ({
      value: String(item.get('id')),
      label: item.get('name'),
    })).toArray();

    content = (
      <Select
        value={localValue}
        onChange={(val) => onChangeItem(val)}
        options={options}
        placeholder={placeholder}
        style={{ width: '100%' }}
        disabled={!editable}
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else {
    const ComponentType = mode === 'buttons' ? Radio.Button : Radio;
    content = (
      <RadioGroup value={localValue} onChange={(e) => onChangeItem(e.target.value)} className={styles.radiogroupList}>
        {items.map((item) => {
          const id = String(item.get('id'));
          const Icon = item.get('icon');
          const IconComponent = Icon ? <Icon /> : null;

          return (
            <ComponentType
              key={id}
              className={cn({ [styles.radioItem]: mode !== 'buttons', readonly: !editable })}
              disabled={!editable}
              value={id}
              checked={localValue === id}
            >
              {IconComponent}
              {item.get('name')}
            </ComponentType>
          );
        })}
      </RadioGroup>
    );
  }

  return content;
};

Radiobutton.propTypes = {
  value: PropTypes.string,
  config: PropTypes.object,
  onChange: PropTypes.func,
  onEndEditing: PropTypes.func,
  editable: PropTypes.bool,
  placeholder: PropTypes.string,
  controlConfig: PropTypes.object,
};

export default memo(Radiobutton);
